import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Modal,
  Row,
  message,
  Typography,
  Col,
  Select,
  Input,
  DatePicker,
  TimePicker,
  Button,
} from "antd";
import { platformApi } from "../../../../api";
import { debounce } from "lodash";

const AttachQuotation = (props) => {
  const { visibility, setVisibility, onSave } = props;
  const [quotations, setQuotations] = useState([]);
  const [quotationData, setQuotationData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const { Title, Text } = Typography;

  // Derive mobile number: prefer quotationPhone, fallback to primary customer contact phone
  const getMobileNo = (object) => {
    if (object.quotationPhone) return object.quotationPhone;
    // Try customer.contacts primary phone
    if (object.customer && Array.isArray(object.customer.contacts)) {
      const primary = object.customer.contacts.find(c => c.type === "Primary");
      if (primary && primary.phone) return primary.phone;
      // Any contact phone as last resort
      const any = object.customer.contacts.find(c => c.phone);
      if (any && any.phone) return any.phone;
    }
    return "No Mobile";
  };

  // Track selected quotations in a ref to avoid stale closures in debounced search/callbacks
  const quotationsRef = useRef(quotations);
  useEffect(() => {
    quotationsRef.current = quotations;
  }, [quotations]);

  const getQuotations = () => {
    platformApi
      .get(`/api/quotation?limit=10`)
      .then((result) => {
        const {
          data: {
            code = 0,
            response: { data = [] } = {},
          } = {},
        } = result || {};

        if (code === 200 && Array.isArray(data)) {
          // Limit initial list to 10 items to ensure super-fast initial rendering
          setQuotationData(data.slice(0, 10));
        } else {
          setQuotationData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching quotations:", error);
        message.error("Unable to get quotations", 2);
      });
  };

  const onSearchQuotation = (search) => {
    platformApi
      .post("/api/quotation/quotationSearch", {
        module: search.module,
        column: search.column,
        searchString: search.searchString,
        fields: search.fields,
        size: 50,
        page: 1,
        except: null,
      })
      .then((res) => {
        const {
          data: {
            response: { data: searchResults = [] } = {},
          } = {},
        } = res || {};

        if (Array.isArray(searchResults)) {
          setQuotationData((prevData) => {
            const currentData = Array.isArray(prevData) ? prevData : [];
            // Keep only the selected items from the previous list
            const selectedSet = new Set(quotationsRef.current);
            const selectedItems = currentData.filter((item) =>
              item && selectedSet.has(item.id)
            );

            // Merge selected items with the new search results (avoiding duplicates)
            const selectedIds = new Set(selectedItems.map((item) => item.id));
            const merged = [...selectedItems];
            searchResults.forEach((item) => {
              if (item && !selectedIds.has(item.id)) {
                merged.push(item);
              }
            });
            return merged;
          });
        }
      })
      .catch((err) => {
        console.error("Error searching quotations:", err);
      });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((upperValue) => {
        const searchVal = (upperValue || "").trim();
        if (!searchVal) {
          getQuotations();
        } else {
          const search = {
            module: "quotations",
            column: "search",
            searchString: searchVal,
            searchColumns: ["quotationId", "quotationPhone"],
            setValue: setQuotationData,
          };
          onSearchQuotation(search);
        }
      }, 300),
    []
  );

  const handleSearch = (value) => {
    const upperValue = (value || "").toUpperCase();
    setSearchValue(upperValue);
    debouncedSearch(upperValue);
  };

  const handleChange = (data) => {
    setQuotations(data);
    setSearchValue("");
    getQuotations();
  };

  const handleBlur = () => {
    setSearchValue("");
    getQuotations();
  };

  const close = () => {
    debouncedSearch.cancel();
    setQuotations([]);
    setQuotationData([]);
    setSearchValue("");
    setVisibility(false);
  };

  useEffect(() => {
    if (visibility) getQuotations();
    return () => {
      debouncedSearch.cancel();
    };
  }, [visibility, debouncedSearch]);

  return (
    <Modal
      width={640}
      title={<Title level={4} style={{ margin: 0, color: "#1e293b", fontWeight: 600 }}>Attach Quotation</Title>}
      visible={visibility}
      cancelText={"Cancel"}
      okText="Attach"
      onOk={() => {
        onSave(quotations), close();
      }}
      onCancel={() => {
        close();
      }}
      wrapClassName="attach-quotation-modal-wrap"
      bodyStyle={{ padding: "24px 32px" }}
    >
      <style>{`
        .attach-quotation-modal-wrap .ant-modal {
          border-radius: 8px;
          overflow: hidden;
        }
        .attach-quotation-modal-wrap .ant-modal-header {
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          padding: 16px 24px;
        }
        .attach-quotation-modal-wrap .ant-modal-footer {
          border-top: 1px solid #e2e8f0;
          padding: 12px 24px;
          background-color: #f8fafc;
        }
        .attach-quotation-select .ant-select-selection {
          border-radius: 6px !important;
          border: 1px solid #cbd5e1 !important;
          transition: all 0.2s ease-in-out;
        }
        .attach-quotation-select .ant-select-selection:hover,
        .attach-quotation-select .ant-select-selection:focus {
          border-color: #0284c7 !important;
          box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15) !important;
        }
        .attach-quotation-select .ant-select-search__field {
          text-transform: uppercase !important;
          min-width: 250px !important;
        }
        .quotation-helper-text {
          display: block;
          margin-bottom: 16px;
          color: #64748b;
          font-size: 13px;
        }
      `}</style>
      <Row type="flex" justify="start" gutter={[16, 16]}>
        <Col span={24}>
          <Select
            showSearch
            showAction={["click", "focus"]}
            mode="multiple"
            placeholder="Search by quotation id or mobile number"
            filterOption={(input, option) => {
              if (option.props.disabled || option.key === "no-data" || option.props.value === "no-data") return false;
              // Find the quotation object to check both quotationId and resolved mobile
              const obj = quotationData.find(q => q.id === option.props.value);
              if (!obj) return false;
              const mobile = getMobileNo(obj);
              const text = `${obj.quotationId || ""} ${mobile}`.toLowerCase();
              return text.includes(input.toLowerCase());
            }}
            searchValue={searchValue}
            onSearch={handleSearch}
            onChange={handleChange}
            onBlur={handleBlur}
            notFoundContent="No quotations found"
            className="attach-quotation-select"
            style={{ width: "100%" }}
          >
            {Array.isArray(quotationData) && quotationData.length > 0 ? (
              quotationData.map((object) => (
                <Select.Option key={object.id} value={object.id}>
                  {object.quotationId} - {getMobileNo(object)}
                </Select.Option>
              ))
            ) : (
              <Select.Option disabled value="no-data">
                No quotations available
              </Select.Option>
            )}
          </Select>
        </Col>
      </Row>
    </Modal>
  );
};

export default AttachQuotation;
